import React, { forwardRef, type Ref } from 'react';

import { InputAdornment, TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';
import { TextArea as DsTextArea, TextInput as DsTextInput, TextInputPill as DsTextInputPill } from 'components';

import { FormFieldHint } from '../FormFieldHint';
import { toDsProps } from './toDsProps';
import { calculateShrink } from './utils';

import type { TextInputPosition } from 'components/TextInput/types';

export type TextFieldProps = Pick<
  MuiTextFieldProps,
  | 'autoComplete'
  | 'autoFocus'
  | 'defaultValue'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'inputProps'
  | 'InputProps'
  | 'label'
  | 'multiline'
  | 'placeholder'
  | 'required'
  | 'rows'
  | 'maxRows'
  | 'size'
  | 'value'
  | 'onBlur'
  | 'onChange'
  | 'onClick'
  | 'onKeyDown'
  | 'type'
  | 'sx'
  | 'variant'
  | 'onFocus'
> & {
  hint?: string;
  wrapHint?: boolean;
  prefix?: string | React.ReactElement;
  shrinkLabel?: boolean;
  suffix?: string | React.ReactElement;
  /**
   * Render the Design System 2.0 **pill** shape rather than the standard 60px input.
   *
   * Refresh-only, and inert while the flag is off: MUI draws no pill, so this cannot be mapped
   * onto the legacy element. It exists because the pill is a separate Figma component set rather
   * than a variant, so which shape a field takes is a call-site decision that `toDsProps` has no
   * way to infer.
   *
   * TODO(HNS-264): folds away with the flag, at which point call sites pick the DS component
   * directly instead of asking this wrapper for a shape.
   */
  pill?: boolean;
  /**
   * Where this field sits in a stacked group, which decides only which corners it rounds.
   *
   * Refresh-only and inert while the flag is off, for the same reason as `pill`: MUI draws no
   * stacked group, so there is nothing to map this onto. It is destructured out below rather than
   * left in `restOfProps` precisely BECAUSE it is inert - React forwards an unrecognised
   * lowercase prop straight to the DOM, so leaving it in would put a stray `position="first"`
   * attribute on the legacy field and move the flag-off DOM.
   *
   * Normally left unset: `FieldGroup` supplies it to each child from the child's index (through
   * `TextInputGroup`), and it reaches this wrapper via `ReactHookForm/TextField`, which spreads
   * what it is given. Set it by hand only for a group the container cannot see through - a child
   * that renders its field inside another wrapper, say.
   *
   * `Inputs/Select` carries the prop the same way and for the same reason, which is what lets a
   * group hold one of each without the two disagreeing about which corners are rounded.
   *
   * TODO(HNS-264): folds away with the flag, at which point call sites hand `position` to the DS
   * component directly instead of through this wrapper.
   */
  position?: TextInputPosition;
};

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  ({ error, disabled, hint, prefix, suffix, shrinkLabel, wrapHint = true, pill, position, ...restOfProps }, ref) => {
    /**
     * The visual-refresh gate lives HERE, not at the call sites — the 73-odd files that reach this
     * component (most through `ReactHookForm/TextField`) keep its MUI-shaped API and never mention
     * the flag. `toDsProps` returns `null` for anything DS 2.0 cannot express, which falls through
     * to the MUI implementation below.
     */
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ error, hint, prefix, suffix, shrinkLabel, wrapHint, ...restOfProps })
      : null;

    if (dsProps) {
      /**
       * Consumed or refused by `toDsProps`, and meaningless on a component with no Emotion and no
       * MUI slots — so they must not be forwarded. Everything else (`name`, `value`, `onChange`,
       * `onBlur`, `type`, `placeholder`, `required`, `autoComplete`, `autoFocus`) passes straight
       * through, which is what keeps the react-hook-form wiring intact.
       */
      const {
        InputProps: _InputProps,
        inputProps: _inputProps,
        fullWidth: _fullWidth,
        label: _label,
        maxRows: _maxRows,
        multiline: _multiline,
        rows: _rows,
        size: _size,
        sx: _sx,
        variant: _variant,
        // Narrowed and re-supplied by `toDsProps`, which is the only place that can prove MUI's
        // `unknown` is something an `<input>` will accept.
        value: _value,
        defaultValue: _defaultValue,
        ...rest
      } = restOfProps;

      /**
       * The legacy wrapper types its ref as `HTMLDivElement` but hands it to MUI as `inputRef`, so
       * it has always pointed at the input. `DsTextInput` forwards to its `Field.Control`, which is
       * that same input — the cast corrects the declared element type rather than changing where
       * the ref lands. `Ref` is invariant in its element, hence the double cast.
       */
      const dsRef = ref as unknown as Ref<HTMLInputElement>;

      /**
       * A multiline field is a different element, so it gets its own branch rather than a prop on
       * the input. `pill` is meaningless here — the pill is a single-line shape — and `toDsProps`
       * has already refused the icon slots a textarea cannot draw.
       *
       * The ref cast mirrors the one below: the legacy wrapper declares `HTMLDivElement` and hands
       * it to MUI as `inputRef`, so for a multiline field it has always pointed at the textarea.
       */
      if (dsProps.multiline) {
        const { multiline: _dsMultiline, ...dsTextAreaProps } = dsProps;

        return (
          <DsTextArea
            {...rest}
            {...dsTextAreaProps}
            disabled={disabled}
            position={position}
            ref={ref as unknown as Ref<HTMLTextAreaElement>}
          />
        );
      }

      /**
       * `TextInputPill` takes no `position` and draws no floating label — it shows the placeholder
       * and hides the label for assistive technology, falling back to the label as the placeholder
       * when none is given. Both shapes accept the same mapped props otherwise.
       *
       * So a `position` passed alongside `pill` goes nowhere, and deliberately is not refused: the
       * pill is always fully rounded by design, which makes the combination a call-site mistake
       * about a corner radius rather than a mapping this wrapper could get wrong. There is no
       * silent drop to guard against - nothing about the field's behaviour or value depends on it.
       */
      return pill ? (
        <DsTextInputPill {...rest} {...dsProps} disabled={disabled} ref={dsRef} />
      ) : (
        <DsTextInput {...rest} {...dsProps} disabled={disabled} position={position} ref={dsRef} />
      );
    }

    return (
      <MuiTextField
        inputRef={ref}
        variant="outlined"
        disabled={disabled}
        error={error}
        InputLabelProps={{
          shrink: calculateShrink({ shrinkLabel, defaultValue: restOfProps.defaultValue, value: restOfProps.value }),
        }}
        helperText={
          hint && (
            <FormFieldHint error={!disabled && !!error} noWrap={!wrapHint}>
              {hint}
            </FormFieldHint>
          )
        }
        {...restOfProps}
        InputProps={{
          ...restOfProps.InputProps,
          startAdornment: prefix && (
            <InputAdornment position="start" sx={{ paddingLeft: 1 }}>
              {prefix}
            </InputAdornment>
          ),
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : (
            restOfProps.InputProps?.endAdornment
          ),
        }}
      />
    );
  }
);
